import { uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react'
import {  approveOrRejectSiteBoqApi, createOrUpdateBoqApi, fileUploadApi, getSiteBoqByIdApi, removeBoqItemApi, submitSiteBoqByIdApi } from '../../utils/ApiEndpointFunctions';
import { useFormik } from 'formik';
import {  boqAddInitalValue, boqAddValidationSchema,  } from '../../utils/Validation';
import Spinwheel from '../../components/common/Spinwheel';
import toast from 'react-hot-toast';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ConfirmationModal from '../../components/modal/ConfirmationModal';
import { getLocalizationStatus } from '../../utils/HelperFuncitons';
import { useUserContext } from '../../context/userContext';
import moment from 'moment';
import StatusModal from '../../components/modal/StatusModal';
import * as XLSX from 'xlsx-js-style';
import ActivityHistory from '../../components/common/ActivityHistory';

const BOQ_UPLOAD_HEADERS = ["SNo", "Name and Details", "Unit", "Total Quantity", "Rate", "Amount"];
const BOQ_UTRA_FIELDS = ["unitType", "quantity", "unitPrice", "amount"];

const hasBoqValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";
const normalizeBoqText = (value) => String(value || "").replace(/\s+/g, " ").trim();
const formatBoqDecimal = (value) => Number(value).toFixed(2);
const formatBoqAmount = (value) => {
  if (!hasBoqValue(value) || !Number.isFinite(Number(value))) {
    return "";
  }

  return Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const formatBoqCurrency = (value) => `₹ ${formatBoqAmount(value || 0)}`;
const generateBoqRowUuid = () => window?.crypto?.randomUUID?.() || uniqueId("boq_row_");
const getHasUTRAFromItem = (item = {}) => BOQ_UTRA_FIELDS.some((field) => hasBoqValue(item?.[field]));
const getBoqDisplayValue = (value) => hasBoqValue(value) ? value : "";
const getBoqDescriptionMinHeight = (value = "") => {
  const textLength = normalizeBoqText(value).length;
  if (textLength <= 55) {
    return "1.45em";
  }
  if (textLength <= 110) {
    return "2.9em";
  }
  return "4.35em";
};
const getTopParentUuid = (item = {}) => item?.L1 || (!item?.parentUuid ? item?.uuid : null);
const getBoqParentGroupStyle = (items = [], index) => {
  const item = items?.[index];
  if (!item) {
    return {};
  }

  const topParentUuid = getTopParentUuid(item);
  const hasParentGroup = !item?.hasUTRA || (!!topParentUuid && item?.level > 1);
  if (!hasParentGroup) {
    return {};
  }

  return {
    backgroundColor: item?.hasUTRA ? "#f2f5fa" : "#eaf0f7",
  };
};
const createBoqUploadError = (message, showAlert = false) => {
  const error = new Error(message);
  error.showAlert = showAlert;
  return error;
};

const createEditableBoqRow = (item = {}) => {
  const hasUTRA = item?.hasUTRA ?? getHasUTRAFromItem(item);
  return {
    ...item,
    uuid: item?.uuid || generateBoqRowUuid(),
    parentUuid: item?.parentUuid || null,
    level: Number(item?.level || 1),
    L1: item?.L1 || null,
    L2: item?.L2 || null,
    L3: item?.L3 || null,
    L4: item?.L4 || null,
    hasUTRA,
    sNo: hasBoqValue(item?.sNo) ? String(item?.sNo).trim() : "",
    nameAndDetails: item?.nameAndDetails || "",
    unitType: hasUTRA ? String(item?.unitType || "").trim() : "",
    quantity: hasUTRA && hasBoqValue(item?.quantity) ? String(item?.quantity) : "",
    unitPrice: hasUTRA && hasBoqValue(item?.unitPrice) ? formatBoqDecimal(item?.unitPrice) : "",
    amount: hasUTRA && hasBoqValue(item?.amount) ? formatBoqDecimal(item?.amount) : "",
    saved: item?.saved ?? true,
    isDraft: item?.isDraft ?? false,
  };
};

const parseBoqWorksheetRows = (worksheet) => {
  const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });
  const headerRow = rows?.[0] || [];
  const headerIndexes = BOQ_UPLOAD_HEADERS.reduce((acc, field) => {
    acc[field] = headerRow.findIndex((header) => normalizeBoqText(header) === field);
    return acc;
  }, {});

  const missingHeaders = BOQ_UPLOAD_HEADERS.filter((field) => headerIndexes[field] === -1);
  if (missingHeaders.length) {
    throw createBoqUploadError(`Missing required fields: ${missingHeaders.join(", ")}`, true);
  }

  const parsedItems = [];
  let parentStack = [];
  const standaloneHeadingRows = [];

  const ensureParentChainComplete = () => {
    const unresolvedParent = parentStack.find((entry) => !entry.hasLeafDescendant);
    if (unresolvedParent) {
      throw createBoqUploadError(`Please check Excel row ${unresolvedParent.excelRow}. This heading does not have any item below it with Unit, Qty, Rate and Amount. Please add the item row or remove this heading.`, true);
    }
  };

  for (let rowIndex = 1; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    const excelRow = rowIndex + 1;
    const isEmptyRow = row.every((cell) => !hasBoqValue(cell));

    if (isEmptyRow) {
      continue;
    }

    const sNo = hasBoqValue(row[headerIndexes["SNo"]]) ? String(row[headerIndexes["SNo"]]).trim() : "";
    const nameAndDetails = normalizeBoqText(row[headerIndexes["Name and Details"]]);
    const unitType = hasBoqValue(row[headerIndexes["Unit"]]) ? String(row[headerIndexes["Unit"]]).trim() : "";
    const quantity = row[headerIndexes["Total Quantity"]];
    const unitPrice = row[headerIndexes["Rate"]];
    const amount = row[headerIndexes["Amount"]];
    const hasUTRA = [unitType, quantity, unitPrice, amount].some((value) => hasBoqValue(value));

    if (!nameAndDetails) {
      throw createBoqUploadError(`Please check Excel row ${excelRow}. Name and Details is missing.`, true);
    }

    if (hasUTRA) {
      if (!unitType) {
        throw createBoqUploadError(`Please check Excel row ${excelRow}. Unit is missing.`, true);
      }
      if (!hasBoqValue(quantity) || Number(quantity) <= 0) {
        throw createBoqUploadError(`Please check Excel row ${excelRow}. Qty is missing or invalid.`, true);
      }
      if (!hasBoqValue(unitPrice) || Number(unitPrice) <= 0) {
        throw createBoqUploadError(`Please check Excel row ${excelRow}. Rate is missing or invalid.`, true);
      }
      if (!hasBoqValue(amount) || Number(amount) <= 0) {
        throw createBoqUploadError(`Please check Excel row ${excelRow}. Amount is missing or invalid.`, true);
      }
    }

    if (sNo) {
      ensureParentChainComplete();
      parentStack = [];

      const rowUuid = generateBoqRowUuid();
      const topLevelItem = createEditableBoqRow({
        uuid: rowUuid,
        parentUuid: null,
        level: 1,
        L1: rowUuid,
        L2: null,
        L3: null,
        L4: null,
        hasUTRA,
        sNo,
        nameAndDetails,
        unitType,
        quantity,
        unitPrice,
        amount,
        saved: true,
        isDraft: false,
      });

      parsedItems.push(topLevelItem);

      if (!hasUTRA) {
        parentStack.push({
          uuid: rowUuid,
          level: 1,
          excelRow,
          hasLeafDescendant: false,
        });
      }
      continue;
    }

    if (!parentStack.length) {
      if (!hasUTRA) {
        standaloneHeadingRows.push({
          excelRow,
          nameAndDetails,
        });
        continue;
      }
      throw createBoqUploadError(`Please check Excel row ${excelRow}. This item is not under any S.No row. Please place it under the correct heading.`, true);
    }

    if (!hasUTRA) {
      if (parentStack.length >= 3) {
        throw createBoqUploadError(`Please check Excel row ${excelRow}. This BOQ goes too deep. Only 4 levels are allowed. Please simplify this section.`, true);
      }

      const rowUuid = generateBoqRowUuid();
      const level = parentStack.length + 1;
      const lineage = parentStack.map((entry) => entry.uuid);
      const levelKeys = ["L1", "L2", "L3", "L4"];
      const hierarchy = levelKeys.reduce((acc, key, index) => {
        if (index < lineage.length) {
          acc[key] = lineage[index];
        } else if (index === lineage.length) {
          acc[key] = rowUuid;
        } else {
          acc[key] = null;
        }
        return acc;
      }, {});

      parsedItems.push(createEditableBoqRow({
        uuid: rowUuid,
        parentUuid: parentStack[parentStack.length - 1]?.uuid || null,
        level,
        ...hierarchy,
        hasUTRA: false,
        sNo: "",
        nameAndDetails,
        unitType: "",
        quantity: "",
        unitPrice: "",
        amount: "",
        saved: true,
        isDraft: false,
      }));

      parentStack.push({
        uuid: rowUuid,
        level,
        excelRow,
        hasLeafDescendant: false,
      });
      continue;
    }

    const rowUuid = generateBoqRowUuid();
    const level = parentStack.length + 1;
    if (level > 4) {
      throw createBoqUploadError(`Please check Excel row ${excelRow}. This BOQ goes too deep. Only 4 levels are allowed.`, true);
    }

    const lineage = parentStack.map((entry) => entry.uuid);
    const levelKeys = ["L1", "L2", "L3", "L4"];
    const hierarchy = levelKeys.reduce((acc, key, index) => {
      if (index < lineage.length) {
        acc[key] = lineage[index];
      } else if (index === lineage.length) {
        acc[key] = rowUuid;
      } else {
        acc[key] = null;
      }
      return acc;
    }, {});

    parsedItems.push(createEditableBoqRow({
      uuid: rowUuid,
      parentUuid: parentStack[parentStack.length - 1]?.uuid || null,
      level,
      ...hierarchy,
      hasUTRA: true,
      sNo: "",
      nameAndDetails,
      unitType,
      quantity,
      unitPrice,
      amount,
      saved: true,
      isDraft: false,
    }));

    parentStack = parentStack.map((entry) => ({
      ...entry,
      hasLeafDescendant: true,
    }));
  }

  ensureParentChainComplete();

  if (standaloneHeadingRows.length) {
    const rowList = standaloneHeadingRows
      .map((row) => `Row ${row.excelRow}: ${row.nameAndDetails}`)
      .join("\n");
    throw createBoqUploadError(
      `Please fix the BOQ sheet before upload.\n\nThese rows are only headings. They do not have S.No, Unit, Qty, Rate or Amount, so they cannot be uploaded:\n${rowList}\n\nPlease remove these heading rows or convert them into proper BOQ rows.`,
      true
    );
  }

  if (!parsedItems.length) {
    throw createBoqUploadError("Data not found", true);
  }

  return parsedItems;
};

const downloadBoqSampleSheet = () => {
  const workbook = XLSX.utils.book_new();
  const worksheetData = [
    BOQ_UPLOAD_HEADERS,
    [1, "Electrical wiring work", "", "", "", ""],
    ["", "Point wiring for admin room", "Point", 12, 850, 10200],
    [2, "Cable laying work", "", "", "", ""],
    ["", "Copper cable 2 core", "", "", "", ""],
    ["", "2.5 sq. mm cable", "Metre", 150, 120.5, 18075],
    [3, "Standalone BOQ item example", "No.", 4, 2500, 10000],
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  worksheet["!cols"] = [
    { wch: 10 },
    { wch: 55 },
    { wch: 14 },
    { wch: 16 },
    { wch: 14 },
    { wch: 16 },
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, "BOQ Sample");
  XLSX.writeFile(workbook, "BOQ-Sample-Sheet.xlsx");
};


function AddSiteBOQ() {
  const [addLoader, setAddLoader] = useState(false)
  const [boqChanges, setBoqChanges] = useState({status:false,isChanges:false})
  const [editingRowIndex, setEditingRowIndex] = useState(null)
  const [editingRowSnapshot, setEditingRowSnapshot] = useState(null)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [rowValidationAttempted, setRowValidationAttempted] = useState({})
  const submitConfirmModal = "submitConfirmModal1"
  const saveAndUploadConfirmModal = "saveAndUploadConfirmModalId"
  const reUploadConfirmModal = "reUploadConfirmModalId"
  const statusModalId = "statusModal1"
  const boqDetailsModalId = "boqDetailsModalId"
  const approveConfirmModalId = "boqApproveConfirmModalId"
  const navigate = useNavigate();
  const params = useParams();
  const { boqId, id } = params;
  const boqRecordId = boqId || id;
  const { userDetails,langPreference} = useUserContext();
  const [confirmationMsg, setConfirmationMsg] = useState('');
  const [updateItem,setUpdateItem] = useState({index:null,loading:false})
  const [statusRedirectPath,setStatusRedirectPath] = useState(null)
  const location = useLocation();
  const [isUpload,setUpload] = useState(false)
  const [download,setDownload] = useState(false)
  const [approveLoader,setApproveLoader] = useState(false)
  const [boqDetailsSnapshot, setBoqDetailsSnapshot] = useState(null)
  const fileRef = useRef()


  const contractDetailFieldNames = [
    "category",
    "subCategory",
  ];

  const markContractDetailsTouched = () => {
    const existingTouched = boqFormik?.touched || {};
    const updatedTouched = { ...existingTouched };
    contractDetailFieldNames.forEach((field) => {
      updatedTouched[field] = true;
    });
    boqFormik.setTouched(updatedTouched);
  };

  const getContractDetailsErrors = (errors = boqFormik?.errors) => {
    return contractDetailFieldNames.filter((field) => !!errors?.[field]);
  };

  const openBoqDetailsForm = () => {
    setBoqDetailsSnapshot({
      category: boqFormik.values?.category || "",
      subCategory: boqFormik.values?.subCategory || "",
    });
  };

  const cancelBoqDetailsChanges = () => {
    if (boqDetailsSnapshot) {
      boqFormik.setFieldValue("category", boqDetailsSnapshot.category, false);
      boqFormik.setFieldValue("subCategory", boqDetailsSnapshot.subCategory, false);
    }
    setBoqDetailsSnapshot(null);
    setBoqChanges({isChanges:false,status:false});
  };

  const saveBoqChanges = async () => {
    markContractDetailsTouched();
    const validationErrors = await boqFormik.validateForm();
    const contractErrors = getContractDetailsErrors(validationErrors);
    if (contractErrors.length) {
      return;
    }
    
    if(boqFormik?.values?._id){
      const payload = {
        _id:boqFormik?.values._id,
        category:boqFormik?.values?.category,
        subCategory:boqFormik?.values?.subCategory,
        type:"save",
      }
      try {
        setBoqChanges({...boqChanges,status:true})
        const res =  await submitSiteBoqByIdApi(payload);
        if (res.status === 200) {
          toast.success(res.data.message);
            setBoqDetailsSnapshot(null)
            getSiteBoqById()
            document.getElementById("boqDetailsModalCloseBtn")?.click()
        }
      } catch (error) {
        error && error.response ?
          toast.error(error.response?.data?.message) :
          toast.error("something went wrong");
      }
      setBoqChanges({isChanges:false,status:false})
    } else {
      setBoqChanges({isChanges:false,status:false})
      setBoqDetailsSnapshot(null)
      document.getElementById("boqDetailsModalCloseBtn")?.click()
    }
  }

  const boqSubmit = async (values) => {
    if(values?._id){
      const payload = {
        _id:values._id,
        category:values?.category,
        subCategory:values?.subCategory,
        type:"submit",
      }
      try {
        setAddLoader(true)
        const res =  await submitSiteBoqByIdApi(payload);
        if (res.status === 200) {
          toast.success(res.data.message);
          document.getElementById(submitConfirmModal).click()
          setConfirmationMsg(res?.data?.message || `BOQ ${res.data?.data?.boqNumber || ""} is successfully submitted.`)
          setStatusRedirectPath("/boq")
          document.getElementById('statusModalBtn').click()
        }
      } catch (error) {
        error && error.response ?
          toast.error(error.response?.data?.message) :
          toast.error("something went wrong");
      }
      setAddLoader(false)
    }
  }

  const boqFormik = useFormik({
    initialValues: boqAddInitalValue,
    onSubmit: boqSubmit,
    validationSchema: boqAddValidationSchema(langPreference),
  })

  const boqStatus = boqFormik?.values?.status?.toLowerCase();
  const isExistingBoq = !!boqFormik?.values?._id;
  const canEditBoq = (!isExistingBoq && !boqRecordId) || (isExistingBoq && !!boqStatus && userDetails?.roleId?.permission?.includes("boqUpdate") && boqStatus !== "approved" && boqStatus !== "rejected");
  const canSubmitBoq = isExistingBoq && canEditBoq && boqStatus !== "submitted";
  const canApproveBoq = isExistingBoq && !!boqStatus && userDetails?.roleId?.permission?.includes("boqApprove") && boqStatus !== "created" && boqStatus !== "approved" && boqStatus !== "rejected";
  const canDownloadBoq = boqStatus === "approved" && userDetails?.roleId?.permission?.includes("boqRead");

  const getSiteBoqById = async () => {
    const res = await getSiteBoqByIdApi(boqRecordId || boqFormik?.values?._id)
    if (res.status === 200) {
      const data = res.data?.data
      if(data){
        const list = data?.items?.map(ele=>{
          return createEditableBoqRow({
            ...ele,
            saved:true,
            isDraft:false,
          })
        })
        boqFormik.setValues(data)
        boqFormik.setFieldValue("items",list)
      }
    }
  }

  useEffect(() => {
    if(boqRecordId){
      getSiteBoqById(boqRecordId)
    }
  }, [boqRecordId])

  const handleConfirmation = (isConvert) => {
    boqFormik.handleSubmit();
  }

  const handleBoqApproval = async () => {
    try {
      setApproveLoader(true);
      const res = await approveOrRejectSiteBoqApi({ id: boqFormik?.values?._id, status: "approved" })
      if (res.status === 200) {
        toast.success(res.data?.message)
        setConfirmationMsg(res?.data?.message || `BOQ ${boqFormik?.values?.boqNumber || ""} is successfully approved.`)
        setStatusRedirectPath(null)
        document.getElementById('statusModalBtn').click()
        getSiteBoqById()
      }
    } catch (error) {
      console.log(error)
      error && error.response ?
        toast.error(error.response?.data?.message) :
        toast.error("something went wrong");
    }
    setApproveLoader(false);
  }

  const exportToExcel = async () => {
    if (!boqFormik?.values?.boqNumber) {
      return;
    }

    try {
      setDownload(true);
      const siteContract = boqFormik?.values?.siteContract || {};
      const workbook = XLSX.utils.book_new();
      const worksheetData = [
        [`BOQ ${boqFormik?.values?.boqNumber || ""}`],
        ["Contract Date", formatDisplayDate(siteContract.contractDate), "Date of Start", formatDisplayDate(siteContract.contractStartDate)],
        ["Date of Completion", formatDisplayDate(siteContract.completionDate), "Extended Completion Date", formatDisplayDate(siteContract.extendedCompletionDate)],
        ["Actual Date", formatDisplayDate(siteContract.actualDate), "Incr/Decr (%)", siteContract.incDesc ?? 0],
        ["Category", boqFormik?.values?.category || "", "Sub-category", boqFormik?.values?.subCategory || ""],
        ["SNo", "Name and Details", "Unit", "Qty", "Rate (₹)", "Amount (₹)"],
        ...(boqFormik?.values?.items || []).map((item) => {
          const showUTRAValues = item?.hasUTRA !== false;
          return [
            item?.sNo || "",
            item?.nameAndDetails || "",
            showUTRAValues ? item?.unitType || "" : "",
            showUTRAValues && hasBoqValue(item?.quantity) ? Number(item?.quantity) : "",
            showUTRAValues && hasBoqValue(item?.unitPrice) ? Number(item?.unitPrice) : "",
            showUTRAValues && hasBoqValue(item?.amount) ? Number(item?.amount) : "",
          ];
        }),
        ["", "", "", "", "Total Amount", boqFormik?.values?.amount || 0],
        ["", "", "", "", "Incr/Decr Value", boqFormik?.values?.incDescValue || 0],
        ["", "", "", "", "Tax amt", boqFormik?.values?.taxAmt || 0],
        ["", "", "", "", "Grand Total", boqFormik?.values?.grandTotal || 0],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet["!cols"] = [
        { wch: 10 },
        { wch: 70 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, 'BOQ');
      XLSX.writeFile(workbook, `BOQ-${boqFormik?.values?.boqNumber || ""}.xlsx`);
      toast.success("Success")
    } catch (error) {
      toast.error("Failed to download")
      console.log("error", error);
    }
    setDownload(false);
  };

  const getValidationAlertMessage = (errors) => {
    const contractFieldLabels = {
      category: langPreference?.category || "Category",
      subCategory: langPreference?.sub_category || "Sub-category",
    };

    const itemFieldLabels = {
      sNo: langPreference?.serial_no || "S.No",
      nameAndDetails: langPreference?.description_of_item || "Description of item",
      unitType: langPreference?.unit || "Unit",
      quantity: langPreference?.qty || "Qty",
      unitPrice: langPreference?.rate || "Rate",
      amount: langPreference?.amount || "Amount",
    };

    const contractErrors = Object.keys(contractFieldLabels)
      .filter((key) => errors?.[key])
      .map((key) => contractFieldLabels[key]);

    const boqItemErrors = (errors?.items || [])
      .map((itemError, index) => {
        if (!itemError) return null;
        const sNo = boqFormik?.values?.items?.[index]?.sNo || `Row ${index + 1}`;
        const fields = Object.keys(itemFieldLabels)
          .filter((key) => itemError?.[key])
          .map((key) => itemFieldLabels[key]);

        if (!fields.length) return null;
        return `${sNo}: ${fields.join(", ")}`;
      })
      .filter(Boolean);

    const sections = [];

    if (contractErrors.length) {
      sections.push(`BOQ Details: ${contractErrors.join(", ")}`);
    }

    if (boqItemErrors.length) {
      sections.push(`BOQ Items needing attention:\n${boqItemErrors.join("\n")}`);
    }

    return sections.length
      ? `Please fix the following before submitting for approval:\n\n${sections.join("\n\n")}`
      : "";
  };

  const checkValidation = async (btn) => {
    setSubmitAttempted(true)
    boqFormik.setTouched({
      category: true,
      subCategory: true,
      items: boqFormik?.values?.items?.map(() => ({
        sNo: true,
        nameAndDetails: true,
        unitType: true,
        quantity: true,
        unitPrice: true,
        amount: true,
      }))
    })
    const validationErrors = await boqFormik.validateForm();
    const errors = Object.keys(validationErrors || {}) 
    console.log("checkValidation",errors,validationErrors,"btn",btn);
      
    if (errors?.length > 0) {
      const message = getValidationAlertMessage(validationErrors);
      if (message) {
        window.alert(message);
      }
    } else {
      document.getElementById(btn).click()
    }
  }

  const checkSaveValidation = async (value,index) => {
    const itemFields = {
      sNo: true,
      nameAndDetails: true,
      unitType: true,
      quantity: true,
      unitPrice: true,
      amount: true,
    };
    const touchedItems = [...(boqFormik?.touched?.items || [])];
    touchedItems[index] = itemFields;
    boqFormik.setTouched({ ...boqFormik.touched, items: touchedItems }, false);
    setRowValidationAttempted((previous) => ({ ...previous, [index]: true }));

    const validationErrors = await boqFormik.validateForm(boqFormik.values);
    const rowError = validationErrors?.items?.[index];
    if (!rowError) {
      handleBoqItemSave(value,index)
    }
  }

  const handleApprovalModalClose = () => {
     if(statusRedirectPath){
       navigate(statusRedirectPath)
     }
  }

const handleAddItemQty =(index, sNo)=>{
const isUnSaved = boqFormik?.values?.items?.length && boqFormik?.values?.items?.filter(ele=>!ele?.saved)?.length
if(isUnSaved){
  toast.error(langPreference?.save_to_continue || "Please save item")
  return
}

  const item = {
    sNo:"",
    nameAndDetails:"",
    quantity: '',
    unitPrice: '',
    unitType: "",
    amount:"",
    hasUTRA:true,
    level:1,
    L1:null,
    L2:null,
    L3:null,
    L4:null,
    parentUuid:null,
    saved:false,
    isDraft:true,
  }
 let list = []
  boqFormik?.values?.items?.forEach((ele,ind)=>{
    if(index==ind){
      list.push(ele,createEditableBoqRow(item))
    }else{
      list.push(ele)
    }
  })

  if(index=="add"){
    list.push(createEditableBoqRow(item))
  }
  boqFormik.setFieldValue("items", list)
}

const handleRemoveItemQty =async(value,index)=>{
  try {
    let newList = boqFormik.values.items.filter((item,ind)=>ind!=index)
    if(value?.isDraft){
      boqFormik?.setFieldValue("items",newList)
      return
    } 
    const res =  await removeBoqItemApi({_id:value?._id});
    if (res.status === 200) {
      toast.success(res.data.message);
      await getSiteBoqById()
    }
    
  } catch (error) {
    console.log("handleRemoveItemQty error",error);
    error && error.response ?
      toast.error(error.response?.data?.message) :
      toast.error("something went wrong");
  }
}

 const handleBoqItemSave =async(values,index)=>{
  try {
    setUpdateItem({index,loading:true})
    const res =  await createOrUpdateBoqApi({...boqFormik?.values,items:[values]});
    if (res.status === 200) {
      toast.success(res.data.message);
      const data = res?.data?.data
     const savedItem = {
       ...values,
       ...(data?.items?.[0] || {}),
       saved: true,
       isDraft: false,
     };
     const nextItems = [...boqFormik.values.items];
     nextItems[index] = savedItem;
     const nextValues = {
       ...boqFormik.values,
       items: nextItems,
       grandTotal: data?.grandTotal,
       incDescValue: data?.incDescValue,
       amount: data?.amount,
       taxAmt: data?.taxAmt,
       _id: data?._id,
       boqNumber: data?.boqNumber,
     };
     const touchedItems = [...(boqFormik?.touched?.items || [])];
     touchedItems[index] = {};

     await boqFormik.setValues(nextValues, false);
     boqFormik.setTouched({ ...boqFormik.touched, items: touchedItems }, false);
     await boqFormik.validateForm(nextValues);
     setRowValidationAttempted((previous) => {
       const next = { ...previous };
       delete next[index];
       return next;
     });
     setEditingRowIndex(null)
     setEditingRowSnapshot(null)
    }
  } catch (error) {
    console.log("error",error);
    
    error && error.response ?
      toast.error(error.response?.data?.message) :
      toast.error("something went wrong");
  }
  setUpdateItem({index:null,loading:false})
 }


const handleChange =(e,index,item,type)=>{
  const {name,value} = e?.target
  if(isNaN(Number(value)) && type=="number"){
    return
  }
  
  boqFormik.handleChange(e)
  boqFormik.handleBlur(e)

  if(item && (name?.includes("unitPrice") || name?.includes("quantity") || name?.includes("unitType") || name?.includes("amount"))){
    let unitType = item?.unitType || ""
    let unitPrice = item?.unitPrice || ""
    let quantity = item?.quantity || ""
    let amount = item?.amount || ""

    if(name?.includes("unitType")){
      unitType = value
    }else if(name?.includes("unitPrice")){
      unitPrice = value
      amount = quantity && unitPrice ? formatBoqDecimal(Number(quantity) * Number(unitPrice)) : ""
      boqFormik.setFieldValue(`items[${index}].amount`, amount)
    }else if(name?.includes("quantity")){
      quantity = value
      amount = quantity && unitPrice ? formatBoqDecimal(Number(quantity) * Number(unitPrice)) : ""
      boqFormik.setFieldValue(`items[${index}].amount`, amount)
    }else if(name?.includes("amount")){
      amount = value
    }

    const hasUTRA = getHasUTRAFromItem({
      unitType,
      quantity,
      unitPrice,
      amount,
    });
    boqFormik.setFieldValue(`items[${index}].hasUTRA`, hasUTRA)
  }

  if(item?.saved){
    boqFormik.setFieldValue(`items[${index}].saved`,false)
    boqFormik.setFieldValue(`items[${index}].isDraft`,true)
  }
}

const getTotalAmt =(items)=>{
  let total =0
  items?.forEach(ele=>{
    total+=Number(ele?.quantity)*Number(ele?.unitPrice)
  })
  return total
}


const handleUploadAndSave =async(file,data)=>{
  try {
    setUpload(true)
    const formData = new FormData();
    formData.append('file', file);
    formData.append("type", "boq")
    const res = await fileUploadApi(formData)
    if (res.status == 200 && res.data?.status === 1) {
      let uploadFile = res?.data?.data
      
     const result =  await createOrUpdateBoqApi({...boqFormik?.values,items:data,uploadFile});
      if (result.status === 200) {
      toast.success(result.data.message);
      const data = result?.data?.data
      boqFormik.setFieldValue("_id",data?._id)
      boqFormik.setFieldValue('uploadFile',uploadFile)
      document.getElementById("saveAndUploadConfirmModal").click()
    }
    setUpload(false)
  }
  } catch (error) {
    setUpload(false)
  }
}

const handleFileUpload = async (e) => {
  try {
    const file = e.target.files[0];
    const fileSplitArr = file?.name?.split(".")
    const fileExtension = fileSplitArr?.[fileSplitArr?.length-1]
    const allowedExtensions = ['xlsx','xls']

    if(!allowedExtensions?.includes(fileExtension)){
      toast.error(`Only xlsx, xls file allow`);
      return
    }
    
    if (file) {
      setUpload(true)
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const filteredData = parseBoqWorksheetRows(worksheet);
          if(filteredData?.length==0){
            fileRef.current.value = null
            setUpload(false)
            toast.error(`Data not found`);
            return
          }
          handleUploadAndSave(file,filteredData)
        } catch (error) {
          fileRef.current.value = null
          setUpload(false)
          if (error?.showAlert) {
            window.alert(error?.message || "Failed to read BOQ file");
          } else {
            toast.error(error?.message || "Failed to read BOQ file");
          }
        }
      };
      reader.readAsArrayBuffer(file);
    }

  } catch (error) {
    fileRef.current.value = null
    console.log("error",error);
    setUpload(false)
    toast.error("Failed to upload")
  }
}

const formatDisplayDate = (value) => value ? moment(value).format("DD/MM/YYYY") : "-";
const getDescriptionPreview = (value = "") => {
  const normalized = String(value).replace(/\s+/g, " ").trim();
  return normalized;
};

const handleEditRow = (index) => {
  setEditingRowSnapshot(JSON.parse(JSON.stringify(boqFormik?.values?.items?.[index] || {})));
  setEditingRowIndex(index);
};

const handleCancelEditRow = (index) => {
  setRowValidationAttempted((previous) => {
    const next = { ...previous };
    delete next[index];
    return next;
  });
  if (boqFormik?.values?.items?.[index]?.isDraft) {
    handleRemoveItemQty(boqFormik?.values?.items?.[index], index);
    setEditingRowIndex(null);
    setEditingRowSnapshot(null);
    return;
  }

  if (editingRowSnapshot) {
    boqFormik.setFieldValue(`items[${index}]`, editingRowSnapshot);
  }
  setEditingRowIndex(null);
  setEditingRowSnapshot(null);
};

const hasTopFieldError = (field) => submitAttempted && !!boqFormik?.errors?.[field];
const hasRowFieldError = (index, field) => (submitAttempted || rowValidationAttempted[index]) && !!boqFormik?.errors?.items?.[index]?.[field];
const getErrorCellClass = (hasError) => hasError ? "border border-danger rounded px-2 py-1 bg-danger bg-opacity-10" : "";
const getTopFieldClass = (field) => hasTopFieldError(field) ? "border border-danger rounded px-2 py-1 bg-danger bg-opacity-10" : "";
const isContractDetailsInvalid = getContractDetailsErrors().length > 0;

  return (
    <>
      <div className="dash-nav1">
        <div className="align-items-center d-block d-lg-flex justify-content-between" style={{paddingBottom:"4px"}}>
      <div className="back"><span onClick={() => navigate(location?.state?.path ? location?.state?.path : -1, { state: { ...location?.state } })} className="back cursor-pointer" ><i className="bi bi-chevron-left"></i> {langPreference?.back || "Back"}</span></div>
          <h3 className="mb-0 font-blue" style={{fontSize:"24px", lineHeight:"1.2"}}>{isExistingBoq ? langPreference?.contract_management || "Contract Management" : langPreference?.add_contract_management || "Add Contract Management"} </h3>
          <div>            
            {canSubmitBoq && <button type="button" disabled={addLoader} className="mb-1 mb-sm-1 sbm-purchase" style={{fontSize:"14px", padding:"8px 14px", minHeight:"unset"}} onClick={() => {
              if (boqFormik.values?.items.filter(item => item.saved)?.length) {
                if(boqFormik.values?.items.filter(item => item.isDraft)?.length){
                  toast.error(langPreference?.save_to_continue ||  "Please save items")
                  return
                }
                checkValidation("submitModalBtn1")
              } else toast.error(langPreference?.please_select_items || "Please select items")
            }}>
              {addLoader ? <Spinwheel sizeClass={"spin-small"} /> : langPreference?.submit_for_approval || "Submit for approval"}
            </button>}
          </div>
        </div>
      </div>
      <div className="dash-table" style={{marginTop:"10px"}}>
        <div className="container">
          <div className="row gap-3 gap-xl-0">
            <div className="col-12">
              <div className="">
                <div>
                  <div className="form-details">
                    <div className='border rounded p-3' style={{backgroundColor:"#fafafa"}}>
                        <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap border-bottom pb-3 mb-3'>
                          <div className='d-flex align-items-center gap-4 flex-wrap'>
                            <div>
                              <span className='fw-bold'>{langPreference?.boq_number || "BOQ Number"}: </span>
                              <span>{boqFormik?.values?.boqNumber || "-"}</span>
                            </div>
                            <div className={getTopFieldClass("category")}>
                              <span className='fw-bold'>{langPreference?.category || "Category"}: </span>
                              <span>{boqFormik?.values?.category || "-"}</span>
                            </div>
                            <div className={getTopFieldClass("subCategory")}>
                              <span className='fw-bold'>{langPreference?.sub_category || "Sub-category"}: </span>
                              <span>{boqFormik?.values?.subCategory || "-"}</span>
                            </div>
                          </div>
                          {canEditBoq && <button type="button" className='add-vendor mb-0' data-bs-toggle="modal" data-bs-target={`#${boqDetailsModalId}`} onClick={openBoqDetailsForm}>
                            {langPreference?.edit || "Edit"}
                          </button>}
                        </div>
                        <div className='row row-cols-2 row-cols-md-3 row-cols-xl-5 g-3'>
                          <div><div className='fw-bold'>{langPreference?.total_amount || "Total Amount"}</div><div>{formatBoqCurrency(boqFormik?.values?.amount)}</div></div>
                          <div><div className='fw-bold'>{langPreference?.inc_dec_value || "Inc/Dec value"}</div><div>{formatBoqCurrency(boqFormik?.values?.incDescValue)}</div></div>
                          <div><div className='fw-bold'>{langPreference?.tax_type || "Tax Type"}</div><div>{String(boqFormik?.values?.taxType || "-").toUpperCase()}</div></div>
                          <div><div className='fw-bold'>{langPreference?.tax_amount || "Tax amt"}</div><div>{formatBoqCurrency(boqFormik?.values?.taxAmt)}</div></div>
                          <div><div className='fw-bold'>{langPreference?.grand_total || "Grand Total"}</div><div>{formatBoqCurrency(boqFormik?.values?.grandTotal)}</div></div>
                        </div>
                    </div>
                  </div>
                </div>
                <>
                      <div className='mb-3  border-0 m-0 p-0' >
                        {/* First Row: Basic Details */}
                        <div className='w-100 mt-1'>
                          <div className='d-flex justify-content-between align-items-center gap-2 flex-wrap'>
                         <h3 className="mb-0 font-blue" style={{fontSize:"24px", lineHeight:"1.2"}}>{langPreference?.boq_items || "BOQ Items"} </h3>
                        {canEditBoq && <div className='d-flex align-items-center gap-2 flex-wrap justify-content-end'>
                        <button type="button" className="add-vendor mb-0 d-inline-flex align-items-center justify-content-center" style={{minHeight:"38px"}} onClick={downloadBoqSampleSheet}>
                        {langPreference?.download_sample_sheet || "Download Sample Sheet"}
                        </button>
                        <button type="button"  className="add-vendor mb-0 d-inline-flex align-items-center justify-content-center" style={{minHeight:"38px"}} onClick={(e)=>{
                          if(!isUpload){
                            fileRef.current.value = null
                            if(boqFormik?.values?.items?.filter(ele=>ele?.saved)?.length){
                              document.getElementById("reUploadConfirmModal").click()
                            }else{
                              fileRef.current.click()

                            }
                          }
                        }} >
                        {isUpload ? <Spinwheel sizeClass={"spin-small"} /> : langPreference?.upload_and_save || "Upload and Save"}
                        </button>
                        </div>}
                        <input type='file' accept=".xlsx, .xls" style={{display:'none'}} ref={fileRef} disabled={isUpload} onChange={(e) => {!isUpload && handleFileUpload(e)}}/>
                          </div>
                          <div className="">
                            <table className="table d-table mt-2" style={{position:'relative'}} >
                              <thead style={{position:'sticky', top: '75px', backgroundColor: '#fbfbfb', fontSize:"14px"}}>
                                <tr>
                                <th style={{whiteSpace:"nowrap", minWidth:"55px", fontSize:"14px"}}>{langPreference?.serial_no || "S.No"}</th>
                                  <th>{langPreference?.description_of_item || "Description of item"}</th>
                                    <th style={{whiteSpace:"nowrap", minWidth:"70px", fontSize:"14px"}}>{langPreference?.unit || "Unit"}</th>
                                   <th style={{whiteSpace:"nowrap", minWidth:"95px", fontSize:"14px", textAlign:"right"}}>{langPreference?.qty || "Qty"}</th>
                                    <th style={{whiteSpace:"nowrap", minWidth:"95px", fontSize:"14px", textAlign:"right"}}>{langPreference?.rate || "Rate"} (₹)</th>
                                    <th style={{whiteSpace:"nowrap", minWidth:"140px", fontSize:"14px", textAlign:"right"}}>{langPreference?.amount || "Amount"} (₹)</th>
                                </tr>
                              </thead>
                              <tbody className=''>
                              {boqFormik?.values?.items?.map((ele, index) => {
                                const isRowEditable = ele?.isDraft || editingRowIndex === index;
                                const rowHasError = submitAttempted && !!boqFormik?.errors?.items?.[index];
                                const parentGroupStyle = getBoqParentGroupStyle(boqFormik?.values?.items, index);
                                const descriptionMinHeight = getBoqDescriptionMinHeight(boqFormik?.values?.items?.[index]?.nameAndDetails);
                                return (
                               <tr key={index} style={{fontSize:"15px", verticalAlign:"middle", backgroundColor: rowHasError ? "#fff5f5" : "transparent"}}>
                                   <td style={parentGroupStyle}>
                                      <div className={getErrorCellClass(hasRowFieldError(index, "sNo"))}>
                                      {isRowEditable ? (
                                        <input
                                          type="text"
                                          className={`form-control ${hasRowFieldError(index, "sNo") ? "border-danger" : ""}`}
                                          name={`items[${index}].sNo`}
                                          value={boqFormik?.values?.items?.[index]?.sNo}
                                          onChange={(e)=>handleChange(e,index,ele,"text")}
                                          onBlur={boqFormik.handleBlur}
                                          disabled={updateItem?.loading || !isRowEditable}
                                          style={{height:"30px",width:"60px"}}
                                        />
                                      ) : (
                                        <div className='py-1' style={{fontSize:"15px", fontWeight:"500"}}>{getBoqDisplayValue(boqFormik?.values?.items?.[index]?.sNo)}</div>
                                      )}
                                      </div>
                                    </td>
                                    <td style={parentGroupStyle}>
                                      <div className={getErrorCellClass(hasRowFieldError(index, "nameAndDetails"))}>
                                      {isRowEditable ? (
                                        <textarea
                                          type="text"
                                          className={`form-control ${hasRowFieldError(index, "nameAndDetails") ? "border-danger" : ""}`}
                                          name={`items[${index}].nameAndDetails`}
                                          value={boqFormik?.values?.items?.[index]?.nameAndDetails}
                                          onChange={(e)=>handleChange(e,index,ele,"text")}
                                          onBlur={boqFormik.handleBlur}
                                          disabled={updateItem?.loading}
                                          rows={4}
                                        />
                                      ) : (
                                        <div
                                          title={boqFormik?.values?.items?.[index]?.nameAndDetails || ""}
                                        >
                                          <div style={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            whiteSpace: "initial",
                                            lineHeight: "1.45",
                                            minHeight: descriptionMinHeight,
                                            minWidth: "280px",
                                            overflowWrap: "anywhere",
                                            fontSize: "15px",
                                            padding: "4px 0"
                                          }}>
                                            {getDescriptionPreview(boqFormik?.values?.items?.[index]?.nameAndDetails)}
                                          </div>
                                        </div>
                                      )}
                                      </div>
                                     </td>
                                    <td style={parentGroupStyle}>
                                      <div className={getErrorCellClass(hasRowFieldError(index, "unitType"))}>
                                      {isRowEditable ? (
                                        <input
                                          type="text"
                                          className={`form-control ${hasRowFieldError(index, "unitType") ? "border-danger" : ""}`}
                                          name={`items[${index}].unitType`}
                                          value={boqFormik?.values?.items?.[index]?.unitType}
                                          onChange={(e)=>handleChange(e,index,ele,"text")}
                                          onBlur={boqFormik.handleBlur}
                                          disabled={updateItem?.loading || !isRowEditable}
                                          style={{height:"30px",width:"60px"}}
                                        />
                                      ) : (
                                        <div className='py-1' style={{fontSize:"15px", fontWeight:"500"}}>{getBoqDisplayValue(boqFormik?.values?.items?.[index]?.unitType)}</div>
                                      )}
                                      </div>
                                    </td>
                                    <td style={parentGroupStyle}>
                                    <div className={getErrorCellClass(hasRowFieldError(index, "quantity"))}>
                                    {isRowEditable ? (
                                      <input
                                          type="text"
                                          className={`form-control text-end ${hasRowFieldError(index, "quantity") ? "border-danger" : ""}`}
                                          name={`items[${index}].quantity`}
                                          value={boqFormik?.values?.items?.[index]?.quantity}
                                          onChange={(e)=>(e.target?.value?.split(".")[1]?.length < 3 || e.target?.value?.split(".")[1]?.length === undefined) && handleChange(e,index,ele,"number")}
                                          onBlur={boqFormik.handleBlur}
                                          disabled={updateItem?.loading || !isRowEditable}
                                          style={{height:"30px",width:"80px"}}
                                        />
                                    ) : (
                                      <div className='py-1 text-end' style={{fontSize:"15px", fontWeight:"500"}}>{formatBoqAmount(boqFormik?.values?.items?.[index]?.quantity)}</div>
                                    )}
                                    </div>
                                     </td>
                                    <td style={parentGroupStyle}>
                                      <div className={getErrorCellClass(hasRowFieldError(index, "unitPrice"))}>
                                      {isRowEditable ? (
                                        <input
                                          type="text"
                                          className={`form-control text-end ${hasRowFieldError(index, "unitPrice") ? "border-danger" : ""}`}
                                          name={`items[${index}].unitPrice`}
                                          value={boqFormik?.values?.items?.[index]?.unitPrice}
                                          onChange={(e)=>(e.target?.value?.split(".")[1]?.length < 3 || e.target?.value?.split(".")[1]?.length === undefined) && handleChange(e,index,ele,"number")}
                                          onBlur={boqFormik.handleBlur}
                                          disabled={updateItem?.loading || !isRowEditable}
                                          style={{height:"30px",width:"80px"}}
                                        />
                                      ) : (
                                        <div className='py-1 text-end' style={{fontSize:"15px", fontWeight:"500"}}>{formatBoqAmount(boqFormik?.values?.items?.[index]?.unitPrice)}</div>
                                      )}
                                      </div>
                                    </td>
                                    <td style={parentGroupStyle}>
                                      <div className={getErrorCellClass(hasRowFieldError(index, "amount"))}>
                                      {isRowEditable ? (
                                        <input
                                          type="text"
                                          className={`form-control text-end ${hasRowFieldError(index, "amount") ? "border-danger" : ""}`}
                                          name={`items[${index}].amount`}
                                          value={boqFormik?.values?.items?.[index]?.amount}
                                          onChange={(e)=>(e.target?.value?.split(".")[1]?.length < 3 || e.target?.value?.split(".")[1]?.length === undefined) && handleChange(e,index,ele,"number")}
                                          onBlur={boqFormik.handleBlur}
                                          disabled={updateItem?.loading || !isRowEditable}
                                          style={{height:"30px",width:"150px"}}
                                        />
                                      ) : (
                                        <div className='py-1 text-end' style={{fontSize:"15px", fontWeight:"500"}}>{formatBoqAmount(boqFormik?.values?.items?.[index]?.amount)}</div>
                                      )}
                                      </div>
                                    </td>
                                  </tr>)})}

                              </tbody>
                            </table>
                            {canEditBoq && !boqFormik?.values?.uploadFile &&
                            <div className='d-flex justify-content-end'>
                            <button  disabled={updateItem?.loading} className='add-vendor'  onClick={() => {  handleAddItemQty("add") }}>+</button>
                            </div>
                              }
                          </div>
                        </div>
                      </div>
                  </>

                  {isExistingBoq && <>
                    <hr />
                    <div>
                      <div className="row row-cols-2 mt-3">
                        <div className="fw-bold">{langPreference?.status || "Status"}</div>
                        <div className='text-capitalize'>{getLocalizationStatus(langPreference,boqFormik?.values?.status)}</div>
                      </div>
                      <ActivityHistory data={boqFormik.values} language={langPreference} />
                    </div>
                    <div className="d-flex justify-content-start gap-2 mt-3">
                      {canApproveBoq && <button type="button" disabled={approveLoader} className="btn btn-primary" data-bs-toggle="modal" data-bs-target={`#${approveConfirmModalId}`}>
                        {approveLoader ? <Spinwheel sizeClass={"spin-small"} /> : langPreference?.approve || "Approve"}
                      </button>}
                      {canDownloadBoq && <button type="button" disabled={download} className="btn btn-primary" onClick={exportToExcel}>
                        {download ? <Spinwheel sizeClass={"spin-small"} /> : langPreference?.download || "Download"}
                      </button>}
                    </div>
                  </>}

              </div>
            </div>

          </div>
        </div>
      </div>
      {/* for po approval */}
      <button className='d-none' id="submitModalBtn1" data-bs-toggle="modal" data-bs-target={`#${submitConfirmModal}`} />
      <ConfirmationModal modalId={submitConfirmModal} handleConfirm={() => handleConfirmation(false)} message={langPreference?.submit_confirmation || <>Are you sure you want to <br /> Submit ?</>} />
      <button className='d-none' id="statusModalBtn" data-bs-toggle="modal" data-bs-target={`#${statusModalId}`} />
      <StatusModal
        modalId={statusModalId}
        message={confirmationMsg}
        handleClose={handleApprovalModalClose}
      />
      <button className='d-none' id="saveAndUploadConfirmModal" data-bs-toggle="modal" data-bs-target={`#${saveAndUploadConfirmModal}`} />
      <StatusModal
        modalId={saveAndUploadConfirmModal}
        message={langPreference?.uploaded_success || 'Uploaded successfully, click on "Submit for approval"'}
        handleClose={()=>{
          if(boqRecordId!=boqFormik?.values?._id){
            navigate(`/boq/update/${boqFormik?.values?._id}`)
          }else{
             window.location.reload()
          }
        }}
      />
      <button className='d-none' id="reUploadConfirmModal" data-bs-toggle="modal" data-bs-target={`#${reUploadConfirmModal}`} />
      <ConfirmationModal modalId={reUploadConfirmModal}  handleConfirm={()=>{fileRef.current.click()}}  message={langPreference?.delete_earlier_boq || 'Earlier BOQ will be deleted'} />
      <ConfirmationModal modalId={approveConfirmModalId} handleConfirm={handleBoqApproval} message={langPreference?.approve_confirmation || <>Are you sure you want to <br /> Approve ?</>} />
      <div className="modal fade" id={boqDetailsModalId} tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{langPreference?.edit || "Edit"} {langPreference?.boq_details || "BOQ Details"}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={cancelBoqDetailsChanges}></button>
              <button type="button" id="boqDetailsModalCloseBtn" className="d-none" data-bs-dismiss="modal" aria-hidden="true"></button>
            </div>
            <div className="modal-body">
              <div className='row g-3'>
                <div className="col-md-6">
                  <label htmlFor="category">{langPreference?.category || "Category"}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="category"
                    value={boqFormik?.values?.category}
                    onChange={(e)=>{boqFormik?.handleChange(e);setBoqChanges({...boqChanges,isChanges:true})}}
                    onBlur={boqFormik?.handleBlur}
                    disabled={updateItem?.loading}
                  />
                  {boqFormik?.touched?.category && boqFormik?.errors?.category ? (<div className='req-error'>{boqFormik?.errors?.category}</div>) : null}
                </div>
                <div className="col-md-6">
                  <label htmlFor="subCategory">{langPreference?.sub_category || "Sub-category"}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="subCategory"
                    value={boqFormik?.values?.subCategory}
                    onChange={(e)=>{boqFormik?.handleChange(e);setBoqChanges({...boqChanges,isChanges:true})}}
                    onBlur={boqFormik?.handleBlur}
                    disabled={updateItem?.loading}
                  />
                  {boqFormik?.touched?.subCategory && boqFormik?.errors?.subCategory ? (<div className='req-error'>{boqFormik?.errors?.subCategory}</div>) : null}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={cancelBoqDetailsChanges}>{langPreference?.cancel || "Cancel"}</button>
              <button type="button" className={`${isContractDetailsInvalid ? "unsave-btn" : "save-btn"}`} disabled={boqChanges?.status} onClick={saveBoqChanges}>
                {boqChanges?.status ? <Spinwheel sizeClass={"spin-small"} /> : langPreference?.save || "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
     </>
  )
}

export default AddSiteBOQ
